import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '../config/axios';
import { useAuth } from '../context/AuthContext';
import ReviewModeBanner from '../components/ReviewModeBanner';
import { getMembership, redeemCredits, isMembershipActive } from '../utils/membership';
import {
  loadRazorpayScript,
  createRazorpayOrder,
  verifyRazorpayPayment,
  buildRazorpayPrefill,
  withRazorpayCheckoutConfig,
  pollRazorpayOrderStatus,
} from '../utils/razorpay';

const EventCheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const paymentCompletedRef = useRef(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [membership, setMembership] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('gateway');
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadMembership = useCallback(async () => {
    try {
      const membershipData = await getMembership();
      setMembership(membershipData);
    } catch (error) {
      console.error('Failed to load membership:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('eventCheckout');
    if (!stored) {
      toast.error('No event checkout found.');
      navigate('/events');
      return;
    }
    setCheckoutData(JSON.parse(stored));
    loadMembership();
  }, [navigate, loadMembership]);

  const totalAmount = useMemo(() => {
    if (!checkoutData?.items) return 0;
    return checkoutData.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
  }, [checkoutData]);

  const membershipActive = isMembershipActive(membership);
  const availableCredits = membershipActive ? membership.balance_remaining ?? 0 : 0;
  const canUseCredits = membershipActive && totalAmount > 0 && availableCredits >= totalAmount;
  const creditShortfall = Math.max(0, totalAmount - availableCredits);

  useEffect(() => {
    if (!canUseCredits && paymentMethod === 'credits') {
      setPaymentMethod('gateway');
    }
  }, [canUseCredits, paymentMethod]);

  const finalizeBooking = async (method, razorpayIds = {}) => {
    if (checkoutData.registration?.answers?.length) {
      await axiosInstance.post('/registrations', {
        event_id: checkoutData.registration.eventId,
        answers: checkoutData.registration.answers,
      });
    }

    const items = checkoutData.items.map((item) => ({
      tier_id: item.tier_id,
      quantity: item.quantity,
    }));

    const res = await axiosInstance.post('/bookings', {
      event_id: checkoutData.eventId,
      items,
      payment_method: method,
      razorpay_order_id:   razorpayIds.orderId   || null,
      razorpay_payment_id: razorpayIds.paymentId || null,
    });

    localStorage.removeItem('eventCheckout');
    toast.success('Booking confirmed!');
    navigate(`/bookings/${res.data.id}`);
  };

  const handleGatewayPayment = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setPaymentError('');
    paymentCompletedRef.current = false;
    let stopPolling = null;

    try {
      setSubmitting(true);
      await loadRazorpayScript();

      const order = await createRazorpayOrder({
        backendUrl,
        amount: totalAmount,
        receipt: `evt-${checkoutData.eventId.slice(0, 8)}-${Date.now()}`,
      });

      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Backyard',
        description: 'Event ticket payment',
        order_id: order.order_id,
        prefill: buildRazorpayPrefill(),
        handler: async (response) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          stopPolling?.();
          try {
            await verifyRazorpayPayment({
              backendUrl,
              payload: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            });

            await finalizeBooking('gateway', {
              orderId:   response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
            });
          } catch (error) {
            console.error('Payment verification failed:', error);
            setPaymentError('Payment verification failed. Please try again.');
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            stopPolling?.();
            setSubmitting(false);
          },
        },
        theme: {
          color: '#E64B65',
        },
      };

      const razorpayInstance = new window.Razorpay(withRazorpayCheckoutConfig(options));
      razorpayInstance.on('payment.failed', (response) => {
        stopPolling?.();
        setPaymentError(response?.error?.description || 'Payment failed. Please try again.');
        setSubmitting(false);
      });

      // UPI Collect rescue — see utils/razorpay.js for the why.
      stopPolling = pollRazorpayOrderStatus({
        backendUrl,
        orderId: order.order_id,
        onPaid: async (info) => {
          if (paymentCompletedRef.current) return;
          paymentCompletedRef.current = true;
          try { razorpayInstance.close(); } catch { /* already closed */ }
          try {
            await finalizeBooking('gateway', {
              orderId:   order.order_id,
              paymentId: info.captured_payment_id,
            });
          } catch (err) {
            console.error('Post-poll completion failed:', err);
            setPaymentError('Payment received but booking finalisation failed. Contact support.');
          } finally {
            setSubmitting(false);
          }
        },
      });

      razorpayInstance.open();
    } catch (e) {
      stopPolling?.();
      toast.error('Booking failed');
      setPaymentError('Checkout failed. Please try again.');
      setSubmitting(false);
    }
  };

  const handleFreeCheckout = async () => {
    try {
      setSubmitting(true);
      await finalizeBooking('free');
    } catch (e) {
      toast.error('Booking failed');
      setPaymentError('Checkout failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!checkoutData || submitting) return;

    // Handle free tickets - skip payment gateway
    if (totalAmount === 0) {
      await handleFreeCheckout();
      return;
    }

    if (paymentMethod === 'credits') {
      if (!membershipActive) {
        toast.error('Activate a membership to use credits.');
        return;
      }
      if (availableCredits < totalAmount) {
        toast.error('Not enough credits for this booking.');
        return;
      }

      try {
        setSubmitting(true);
        
        // Use credits via backend API
        await redeemCredits(
          totalAmount,
          'booking',
          checkoutData.eventId,
          `Event booking: ${checkoutData.eventTitle || checkoutData.eventId}`
        );
        
        // Update local membership state
        setMembership(prev => ({
          ...prev,
          balance_remaining: prev.balance_remaining - totalAmount
        }));

        await finalizeBooking('credits');
      } catch (e) {
        const errorMessage = e.response?.data?.detail || 'Failed to use credits. Please try again.';
        toast.error(errorMessage);
        setPaymentError(errorMessage);
        // Reload membership to get accurate balance
        await loadMembership();
      } finally {
        setSubmitting(false);
      }

      return;
    }

    await handleGatewayPayment();
  };

  if (loading || !checkoutData) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562] mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-32 text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="px-6 pt-8">
        <div className="flex items-center gap-3 mb-6" data-testid="event-checkout-header">
          <button
            data-testid="event-checkout-back"
            onClick={() => navigate(`/events/${checkoutData.eventId}`)}
            className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center"
          >
            <i className="ph ph-arrow-left"></i>
          </button>
          <div>
            <h1 className="text-2xl font-extrabold">Checkout</h1>
            <p className="text-sm text-gray-500">One final step to confirm.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6" data-testid="event-checkout-summary">
          <h3 className="text-lg font-bold mb-4">Order Summary</h3>
          <div className="space-y-3">
            {checkoutData.items.map((item) => (
              <div key={item.tier_id} className="flex items-start justify-between">
                <div>
                  <p className="font-bold">{item.tier_name}</p>
                  <p className="text-sm text-gray-500">x{item.quantity}</p>
                </div>
                <p className="font-bold">₹{(item.price * item.quantity).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 mt-4 pt-4 flex items-center justify-between">
            <p className="font-bold text-lg">Total</p>
            <p className="font-extrabold text-xl" data-testid="event-checkout-total">
              ₹{totalAmount.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#F4D35E] rounded-2xl p-4 mb-4" data-testid="event-credits-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-bold">Member credits</p>
                <p className="text-sm text-gray-500 mt-1" data-testid="event-credits-status">
                  {membershipActive
                    ? canUseCredits
                      ? 'Cover the full ticket amount with credits.'
                      : creditShortfall > 0
                      ? `Need ₹${creditShortfall.toLocaleString()} more credits.`
                      : 'Select tickets to unlock credits.'
                    : 'No active membership found.'}
                </p>
              </div>
              <span className="font-bold" data-testid="event-credits-balance">₹{availableCredits.toLocaleString()}</span>
            </div>
            {!membershipActive && (
              <button
                onClick={() => navigate('/memberships')}
                className="text-[#E54562] font-bold text-sm"
                data-testid="event-credits-cta"
              >
                View memberships
              </button>
            )}

            {membershipActive && canUseCredits && (
              <label
                data-testid="event-payment-credits-option"
                className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
                  paymentMethod === 'credits' ? 'border-[#E54562] bg-[#E54562]/10' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  data-testid="event-payment-credits-radio"
                  name="paymentMethod"
                  value="credits"
                  checked={paymentMethod === 'credits'}
                  onChange={() => setPaymentMethod('credits')}
                  className="accent-[#E54562]"
                />
                <div>
                  <p className="font-bold">Pay with credits</p>
                  <p className="text-xs text-gray-500">Use your membership balance</p>
                </div>
              </label>
            )}
          </div>

          <label
            data-testid="event-payment-gateway-option"
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
              paymentMethod === 'gateway' ? 'border-[#E54562] bg-[#E54562]/10' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              data-testid="event-payment-gateway-radio"
              name="paymentMethod"
              value="gateway"
              checked={paymentMethod === 'gateway'}
              onChange={() => setPaymentMethod('gateway')}
              className="accent-[#E54562]"
            />
            <div>
              <p className="font-bold">Pay with Razorpay</p>
              <p className="text-xs text-gray-500">Cards, UPI, Netbanking & more</p>
            </div>
          </label>
        </div>

        {paymentError && (
          <div
            data-testid="event-payment-error"
            className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-3 text-xs font-semibold mt-4"
          >
            {paymentError}
          </div>
        )}

        {user?.review_mode && (
          <ReviewModeBanner
            disabled={submitting}
            amountLabel={`₹${totalAmount.toLocaleString()}`}
            onBypass={handleFreeCheckout}
          />
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100">
        <button
          data-testid="event-confirm-booking"
          onClick={handleConfirm}
          disabled={submitting}
          className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold disabled:opacity-70"
        >
          {submitting 
            ? 'Processing...' 
            : totalAmount === 0 
              ? 'Confirm Free Ticket' 
              : `Pay ₹${totalAmount.toLocaleString()}`}
        </button>
      </div>
    </div>
  );
};

export default EventCheckoutPage;
